import { useEffect, useState } from "react";

import type { DevicesResult } from "./ImageEditor.types";

type VideoDevicesState = {
  devices: MediaDeviceInfo[];
  isLoading: boolean;
  hasPermission: boolean;
  hasCameras: boolean;
  hasMultipleCameras: boolean;
};

let cache: Promise<DevicesResult> | null = null;

// const isTouchDevice = () =>
//   typeof navigator !== "undefined" && navigator.maxTouchPoints > 0;

const getConnectedDevices = async (type: MediaDeviceKind) => {
  const devices = await navigator.mediaDevices.enumerateDevices();

  return devices.filter((device) => device.kind === type);
};

function loadDevices(): Promise<DevicesResult> {
  if (!cache) {
    cache = navigator.permissions
      .query({ name: "camera" as PermissionName })
      .then(async (permission) => {
        if (permission.state !== "granted") {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
          });

          stream.getTracks().forEach((track) => track.stop());
        }

        return getConnectedDevices("videoinput");
      })
      .then((devices) => ({ devices, hasPermission: true }))
      .catch(() => ({ devices: [], hasPermission: false }));
  }

  return cache;
}

export function useVideoDevices(): VideoDevicesState {
  const [result, setResult] = useState<DevicesResult | null>(null);

  useEffect(() => {
    loadDevices().then(setResult);

    let permStatus: PermissionStatus | null = null;

    const handleChange = () => {
      cache = null;
      loadDevices().then(setResult);
    };

    navigator.permissions
      .query({ name: "camera" as PermissionName })
      .then((status) => {
        permStatus = status;
        status.addEventListener("change", handleChange);
      });

    return () => {
      permStatus?.removeEventListener("change", handleChange);
    };
  }, []);

  const devices = result?.devices ?? [];

  return {
    devices,
    isLoading: result === null,
    hasPermission: result?.hasPermission ?? false,
    hasCameras: devices.length > 0,
    hasMultipleCameras: devices.length > 1,
  };
}
